import { ListingsFiltersMatchCriteria } from '@aloft/toolkit-shared';

import { CompSelectionProperty } from '../types.ts';

type RatingRange = {
  min?: number;
  max?: number;
};

type ClientNumberRangeValue = {
  min?: number | '';
  max?: number | '';
  variance?: number | '';
  exact?: number | '';
  percentage?: number | '';
};

const toNumber = (value: number | '' | undefined): number | undefined =>
  typeof value === 'number' ? value : undefined;

export const resolveUadRatingRange = (
  filter: ClientNumberRangeValue | undefined,
  matchCriteria: ListingsFiltersMatchCriteria['quality'] | ListingsFiltersMatchCriteria['condition'],
  subjectRating?: number,
): RatingRange | undefined => {
  if (!filter) return undefined;

  if (matchCriteria === 'min_max_range') {
    const min = toNumber(filter.min);
    const max = toNumber(filter.max);
    if (min === undefined && max === undefined) return undefined;
    return { min, max };
  }

  const center = toNumber(filter.exact) ?? subjectRating;
  const variance = toNumber(filter.variance);
  if (center === undefined || variance === undefined) return undefined;

  return {
    min: Math.max(1, center - variance),
    max: center + variance,
  };
};

export const ratingInRange = (
  rating: number | undefined,
  range: RatingRange | undefined,
  { keepWhileLoading, isLoading }: { keepWhileLoading?: boolean; isLoading?: boolean } = {},
): boolean => {
  if (!range) return true;
  if (rating !== undefined) {
    if (range.min !== undefined && rating < range.min) return false;
    if (range.max !== undefined && rating > range.max) return false;
    return true;
  }
  if (keepWhileLoading && isLoading) return true;
  return false;
};

export const filterByUadRatingFilters = (
  comps: CompSelectionProperty[],
  {
    qualityFilter,
    conditionFilter,
    qualityMatchCriteria,
    conditionMatchCriteria,
    subjectQuality,
    subjectCondition,
    keepIds,
  }: {
    qualityFilter?: ClientNumberRangeValue;
    conditionFilter?: ClientNumberRangeValue;
    qualityMatchCriteria?: ListingsFiltersMatchCriteria['quality'];
    conditionMatchCriteria?: ListingsFiltersMatchCriteria['condition'];
    subjectQuality?: number;
    subjectCondition?: number;
    keepIds?: Set<string>;
  },
): CompSelectionProperty[] => {
  const qualityRange = resolveUadRatingRange(qualityFilter, qualityMatchCriteria, subjectQuality);
  const conditionRange = resolveUadRatingRange(
    conditionFilter,
    conditionMatchCriteria,
    subjectCondition,
  );

  if (!qualityRange && !conditionRange) return comps;

  return comps.filter((comp) => {
    if (keepIds?.has(comp.id)) return true;
    return (
      ratingInRange(comp.uadQualityRating, qualityRange, {
        keepWhileLoading: true,
        isLoading: comp.uadRatingsLoading,
      }) &&
      ratingInRange(comp.uadConditionRating, conditionRange, {
        keepWhileLoading: true,
        isLoading: comp.uadRatingsLoading,
      })
    );
  });
};
